import { useEffect, useState } from "react";
import { Radio, Check, AlertCircle, Loader2 } from "lucide-react";
import { trackEvent } from "@/lib/audit";
import { ManualOffsetSlider } from "@/components/sync/ManualOffsetSlider";
import { useLanguage } from "@/hooks/useLanguage";
import {
  useGlobalTvOffset,
  type SyncState,
} from "@/lib/sync/globalTvOffsetBus";

interface Props {
  /** Optional notifier kept for backward compatibility with Header wiring. */
  onSynced?: (offsetSeconds: number) => void;
  /**
   * Kept for backward compatibility. Sync is a real feature and remains
   * fully usable regardless of whether the currently displayed fixture is
   * synthetic — VerseSport is not a demo app and sync must never look
   * disabled or labelled as demo.
   */
  isDemo?: boolean;
}

/**
 * TV Sync button — single-surface UX.
 *
 * Consolidates state, offset and action into one mobile-first button.
 * No lateral preset list; all interaction flows through the button →
 * ManualOffsetSlider.
 */
export function AudioSyncButton({ onSynced, isDemo: _isDemo = false }: Props) {
  const { t, lang } = useLanguage();
  // _isDemo intentionally ignored: sync flow is identical for real and
  // simulated fixtures so the button is always actionable.
  void _isDemo;
  const { offsetSec, state } = useGlobalTvOffset();
  const [offsetOpen, setOffsetOpen] = useState(false);
  const [showTip, setShowTip] = useState(false);

  // P0 — Audio Sync visibility: external surfaces (Home edu card,
  // tooltips, etc.) can request the manual sheet via window event.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => {
      setOffsetOpen(true);
      void trackEvent("audio_sync_started", { source: "edu_card" });
    };
    window.addEventListener("vs:open-audio-sync", handler as EventListener);
    return () =>
      window.removeEventListener("vs:open-audio-sync", handler as EventListener);
  }, []);

  // P0-DIFFERENTIATOR-01 — one-time educational microtour explaining the
  // TV sync feature. Auto-dismisses after 8s, persists via localStorage.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem("vs_audio_sync_tour_seen") === "1") return;
      const t = window.setTimeout(() => {
        setShowTip(true);
        void trackEvent("audio_sync_tour_shown", {});
      }, 1200);
      return () => window.clearTimeout(t);
    } catch { /* noop */ }
  }, []);
  useEffect(() => {
    if (!showTip) return;
    const id = window.setTimeout(() => dismissTip("auto"), 8000);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTip]);
  const dismissTip = (reason: "ok" | "never" | "auto") => {
    setShowTip(false);
    try { localStorage.setItem("vs_audio_sync_tour_seen", "1"); } catch { /* noop */ }
    void trackEvent("audio_sync_tour_completed", { reason });
  };

  // PR-37 — TV Sync UX rebuild: always show positive context.
  const safeOffset = Math.max(0, offsetSec);

  const getDisplay = (s: SyncState) => {
    switch (s) {
      case "syncing":
        return { label: t("tv_sync_searching"), sub: undefined, icon: "searching", variant: "pending" as const };
      case "listening":
        return { label: t("tv_sync_detecting"), sub: undefined, icon: "detecting", variant: "pending" as const };
      case "analyzing":
        return { label: t("tv_sync_analyzing"), sub: undefined, icon: "analyzing", variant: "pending" as const };
      case "manual_sync":
      case "synced":
        return {
          label: t("tv_sync_synced"),
          sub: `+${safeOffset.toFixed(1)}s ${t("tv_sync_advantage_suffix")}`,
          icon: "check",
          variant: "success" as const,
        };
      case "mic_denied":
      case "unsupported":
      case "low_confidence":
        return {
          label: t("tv_sync_failed"),
          sub: t("tv_sync_retry"),
          icon: "alert",
          variant: "warning" as const,
        };
      case "idle":
      default:
        return safeOffset > 0
          ? {
              label: t("tv_sync_synced"),
              sub: `+${safeOffset.toFixed(1)}s ${t("tv_sync_advantage_suffix")}`,
              icon: "check",
              variant: "success" as const,
            }
          : { label: t("tv_sync_setup"), sub: undefined, icon: "radio", variant: "idle" as const };
    }
  };

  const baseDisplay = getDisplay(state);
  const display =
    baseDisplay.variant === "idle" && !baseDisplay.sub
      ? {
          ...baseDisplay,
          label:
            lang === "EN" ? "Sync TV" : lang === "PT" ? "Sincronizar TV" : "Sincronizar TV",
          sub:
            lang === "EN"
              ? "Listen to the match and tune the second screen"
              : lang === "PT"
              ? "Ouça a partida e ajuste a segunda tela"
              : "Escucha el partido y ajusta la segunda pantalla",
        }
      : baseDisplay;

  const variantClasses = {
    idle: "glass hover:bg-accent text-muted-foreground hover:text-foreground",
    pending: "bg-primary/10 text-primary border border-primary/30 animate-pulse",
    success: "bg-team-overlay-soft text-team-overlay border border-team-overlay",
    warning: "bg-destructive/10 text-destructive border border-destructive/30",
  };

  return (
    <div className="relative flex items-center">
      {showTip && (
        <div role="dialog" aria-label="Audio Sync" className="absolute left-0 top-full z-50 mt-1 w-[280px] rounded-lg border border-primary/60 bg-background/95 p-3 text-[11px] shadow-xl backdrop-blur-md">
          <div className="font-black text-primary">📺 ¿Ves el partido por TV?</div>
          <p className="mt-1 leading-snug text-muted-foreground">
            Sincroniza VerseSport con tu televisión para evitar spoilers y recibir alertas exactamente cuando tú quieras.
          </p>
          <div className="mt-2 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => dismissTip("ok")}
              className="rounded-md bg-primary px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-primary-foreground"
            >
              Entendido
            </button>
            <button
              type="button"
              onClick={() => dismissTip("never")}
              className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground"
            >
              No mostrar nuevamente
            </button>
          </div>
        </div>
      )}
      <button
        onClick={() => {
          setOffsetOpen(true);
        }}
        aria-label={display.label}
        title={display.sub ? `${display.label} · ${display.sub}` : display.label}
        className={`flex min-h-[44px] flex-col items-center justify-center rounded-lg px-3 py-1.5 text-[11px] font-bold transition ${variantClasses[display.variant]}`}
      >
        <span className="flex items-center gap-1">
          {display.icon === "check" && <Check className="h-3.5 w-3.5" />}
          {display.icon === "alert" && <AlertCircle className="h-3.5 w-3.5" />}
          {display.icon === "radio" && <Radio className="h-3.5 w-3.5" />}
          {(display.icon === "searching" || display.icon === "detecting" || display.icon === "analyzing") && (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          )}
          <span>{display.label}</span>
        </span>
        {display.sub && (
          <span className="text-[10px] font-semibold tabular-nums opacity-90">
            {display.sub}
          </span>
        )}
      </button>

      <ManualOffsetSlider
        open={offsetOpen}
        onClose={() => setOffsetOpen(false)}
        onApply={(delta) => {
          onSynced?.(delta);
          if (delta > 0) {
            void trackEvent("audio_sync_completed", { delta, source: "manual" });
          }
        }}
      />
    </div>
  );
}
